interface VisibilityTrackingOptions<T> {
  onVisible?: (items: T[]) => void
}

export function useVisibilityTracking<T extends { id: string }>(
  options: VisibilityTrackingOptions<T> = {},
) {
  const { onVisible } = options

  // Track items that have already been reported
  const reportedItems = new Set<string>()

  // Pending items to report
  let pendingItems: T[] = []

  // WeakMap for automatic memory cleanup
  const elementToItemMap = new WeakMap<Element, T>()

  // Single IntersectionObserver instance
  let observer: IntersectionObserver | null = null

  // Process and report visible items
  const reportVisibleItems = () => {
    if (pendingItems.length === 0)
      return

    const itemsToReport = pendingItems
    pendingItems = []

    // Use queueMicrotask for non-blocking execution
    queueMicrotask(() => {
      onVisible?.(itemsToReport)
    })
  }

  // Initialize observer lazily
  const getObserver = () => {
    if (!observer) {
      observer = new IntersectionObserver(
        (entries) => {
          const newVisibleItems: T[] = []

          for (const entry of entries) {
            // Only process items that are entering the viewport
            if (!entry.isIntersecting || entry.intersectionRatio < 0.5)
              continue

            const item = elementToItemMap.get(entry.target)
            if (!item || reportedItems.has(item.id))
              continue

            // Mark as reported and add to pending
            reportedItems.add(item.id)
            newVisibleItems.push(item)
          }

          if (newVisibleItems.length > 0) {
            pendingItems.push(...newVisibleItems)
            reportVisibleItems()
          }
        },
        {
          // Item must be 50% visible to trigger
          threshold: 0.5,
          // No margin - only track when actually visible
          rootMargin: '0px',
        },
      )
    }
    return observer
  }

  // Track an element
  const track = (element: Element, item: T) => {
    elementToItemMap.set(element, item)
    getObserver().observe(element)
  }

  // Untrack an element
  const untrack = (element: Element) => {
    getObserver().unobserve(element)
  }

  // Track multiple elements at once
  const trackBatch = (elements: Array<{ element: Element, item: T }>) => {
    const obs = getObserver()
    for (const { element, item } of elements) {
      elementToItemMap.set(element, item)
      obs.observe(element)
    }
  }

  // Cleanup
  const cleanup = () => {
    // Report any remaining items
    reportVisibleItems()

    if (observer) {
      observer.disconnect()
      observer = null
    }

    reportedItems.clear()
    pendingItems = []
  }

  // Auto cleanup on unmount
  onBeforeUnmount(cleanup)

  return {
    track,
    untrack,
    trackBatch,
    cleanup,
  }
}
